import { useEffect, useState } from "react";
import {
  fetchDocumentPreviewBlobUrl,
  getDocumentEvents,
  PREVIEWABLE_MIME_TYPES,
} from "../../helpers/documents_helper";
import { unwrap } from "./helpers";

/**
 * Carga eventos del documento + preview blob URL cuando `openDoc` cambia.
 * Libera la blob URL al cerrar o cambiar de documento.
 *
 * Devuelve: { events, eventsLoading, previewUrl, previewLoading, previewError }
 */
export function useDocumentDetail(openDoc) {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (!openDoc) {
      setEvents([]);
      setPreviewError(null);
      return undefined;
    }

    let cancelled = false;
    setEventsLoading(true);
    getDocumentEvents(openDoc.id)
      .then((res) => {
        if (cancelled) return;
        setEvents(unwrap(res) || []);
      })
      .catch(() => {
        if (cancelled) return;
        setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });

    let createdUrl = null;
    if (PREVIEWABLE_MIME_TYPES.has(openDoc.mime_type)) {
      setPreviewLoading(true);
      setPreviewError(null);
      fetchDocumentPreviewBlobUrl(openDoc.id)
        .then((blobUrl) => {
          if (cancelled) {
            window.URL.revokeObjectURL(blobUrl);
            return;
          }
          createdUrl = blobUrl;
          setPreviewUrl(blobUrl);
        })
        .catch((e) => {
          if (!cancelled) {
            setPreviewError(typeof e === "string" ? e : e?.message || "No se pudo cargar la previsualización.");
            setPreviewUrl(null);
          }
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    } else {
      setPreviewUrl(null);
    }

    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
      setPreviewUrl(null);
    };
  }, [openDoc]);

  return { events, eventsLoading, previewUrl, previewLoading, previewError };
}
